import React, {
  EventHandler,
  useEffect,
  useState,
  useRef,
  useCallback
} from 'react';
import { makeCSS } from '../../utils/makeCSS';
import { Typography } from './Typography';
import { Icon } from './Icon';
import { iconFiles } from './Icon/files';
import { SizeVariants } from '../../theme/themes';
import { cx } from '@emotion/css';
import mergeRefs from '../../helpers/MergeRefs';
import { isMobile } from 'react-device-detect';

/**
 * The MenuItem component represents an option that the user
 * an select from the Menu.
 */
/**
 * Note: some icons don't have a fill version, in that case
 * we use the standard one
 */
export interface MenuItemProps {
  /**The value of the menu item */
  value?: string;
  /**The default icon we want to display*/
  itemIcon?: keyof typeof iconFiles;
  /**When MenuItem is chosen or the page of the item is displayed */
  selected?: boolean;
  //in case we want to not use this functionality
  disableSelected?: boolean;
  /** */
  onClick?: EventHandler<React.MouseEvent>;
  disabled?: boolean;
  extraButtonProperties?: {
    component?: string | React.ElementType;
    href?: string;
    download?: boolean;
    datatype?: string;
    [key: string]: any;
  };
  /**for the cases that we want to display a status */
  failedStatus?: boolean;
  mobileVersion?: boolean;
}

const useCSS = makeCSS(({ theme }) => {
  return {
    buttonStyle: {
      outline: 'none',
      background: 'none',
      border: 'none',
      width: '100%',
      cursor: 'pointer',
      '&:focus-visible': {
        borderRadius: theme.borderRadius.lg,

        outline: `1px solid ${theme.color.foreground.primary}`,
        boxShadow: `0px 0px 0px 3px rgba(255, 212, 35, 0.12)`
      },
    },
    root: {
      display: 'flex',
      flexShrink: 0,
      borderRadius: theme.borderRadius.lg,
      alignItems: 'center',
      height: theme.spacing[10],
      paddingLeft: theme.spacing[3],
      paddingRight: theme.spacing[3],
      textAlign: 'left',
      '&:focus-visible': {
        outline: `1px solid ${theme.color.foreground.primary}`,
        boxShadow: `0px 0px 0px 3px rgba(255, 212, 35, 0.12)`
      },
      '&:hover': {
        background: theme.color.overlay.hovered
      },
      '&.disabled': {
        cursor: 'default',
        opacity: 0.4,
        '&:hover': {
          background: 'none'
        }
      }
    },
    mobile: {
      height: theme.spacing[12]
    },
    selected: {
      background: theme.color.overlay.focused,
      // '&:hover': {
      //   background: theme.color.overlay.hovered
      // },
      '&.disabled': {
        '&:hover': {
          background: theme.color.overlay.focused
        }
      }
    },
    valuePadding: {
      padding: '8px 0px',
      marginLeft: theme.spacing[2]
    },
    failedStatus: {
      boxShadow: '0 0 0 2px rgba(218, 49, 67, 0.12)'
    },
    mobileContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyItems: 'center',
      alignItems: 'center',
      width: theme.spacing[14],
      gap: theme.spacing[2],
      flexShrink: 0,
      '&.disabled': {
        cursor: 'default',
        opacity: 0.4
      }
    }
  };
});

const MenuItem = (props: MenuItemProps) => {
  const {
    value,
    itemIcon,
    onClick,
    selected = false,
    disabled = false,
    disableSelected = false,
    extraButtonProperties = { buttonRef: null },
    mobileVersion = false,
    failedStatus = false
  } = props;

  const { buttonRef, fullWidth, ...rest } = extraButtonProperties;

  const classes = useCSS();
  const [focus, setFocus] = useState(false);
  const colorVariant = focus ? '01' : '02';

  const activeIcon = itemIcon + '_fill';
  /**
   * if icon has a fill version use it automatically
   * else use the standard icon
   */
  // const finalIcon =
  //   focus && iconFiles[activeIcon] != undefined ? activeIcon : itemIcon;

  const finalIcon: keyof typeof iconFiles =
    focus && activeIcon in iconFiles
      ? (activeIcon as keyof typeof iconFiles)
      : itemIcon;

  const firstRender = useRef(true);
  useEffect(() => {
    /**
     * checks if  its the first time the component gets rendered and sets the focus equal to the selected.
     * we only need this for the first time
     */
    if (firstRender.current) {
      setFocus(selected);
    }
    firstRender.current = false;
    setFocus(selected || false);
  }, [selected]);

  //we dont want it to be unselected by clicking
  const handleSelected = useCallback(
    (event: React.MouseEvent) => {
      if (!disabled) {
        if (!disableSelected) {
          setFocus((prev) => (selected ? true : !prev));
        }
        if (onClick) {
          onClick(event);
        }
      }
    },
    [selected, onClick]
  );

  return (
    <button
      type="button"
      onClick={handleSelected}
      className={`${classes.buttonStyle}`}
      ref={mergeRefs(buttonRef)}
      {...rest}
    >
      {!mobileVersion ? (
        <div
          className={cx(
            classes.root,
            focus ? classes.selected : '',
            disabled && 'disabled',
            failedStatus ? classes.failedStatus : ''
          )}
        >
          <Icon
            icon={finalIcon}
            colorVariant={colorVariant}
            size={isMobile ? SizeVariants.xl : SizeVariants.md}
          />
          {value && (
            <span className={`${classes.valuePadding}`}>
              <Typography
                variant={isMobile ? 'lg' : 'md'}
                weight="body"
                colorVariant={colorVariant}
              >
                {value}
              </Typography>
            </span>
          )}
        </div>
      ) : (
        <div
          className={cx(
            classes.mobileContainer,
            disabled && 'disabled',
            failedStatus ? classes.failedStatus : ''
          )}
        >
          <Icon
            icon={finalIcon}
            colorVariant={colorVariant}
            size={SizeVariants.xl}
          />
          <Typography variant="sm" weight="body" colorVariant={colorVariant}>
            {value}
          </Typography>
        </div>
      )}
    </button>
  );
};

//const Default = withCubicleFallback(MenuItem);
//export { Default as MenuItem };
export { MenuItem };
